import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import type { ErrorApi } from '../api/errores'
import type { AvisoManual, EtiquetaSemana, RespuestaAviso } from '../api/modelos'

/** Avisos de la semana en modo manual (RF-03, RF-45). Sin semana = la del ciclo. */
export function useAvisosManuales(semana: EtiquetaSemana | '') {
  const api = useApi()
  return useQuery<AvisoManual[], ErrorApi>({
    queryKey: ['avisos-manuales', semana],
    queryFn: ({ signal }) =>
      api.pedir<AvisoManual[]>('/api/avisos-manuales', { parametros: { semana }, senal: signal }),
  })
}

function useRefrescar() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ['avisos-manuales'] })
    // El corte del viernes y Confirmar cuentan las respuestas registradas (RF-06).
    void qc.invalidateQueries({ queryKey: ['ciclo'] })
  }
}

/**
 * La semana del listado viaja como `?semana=` para que el envío y la respuesta
 * caigan en la misma sesión que pinta el listado; vacía = la del ciclo actual.
 */
const deSemana = (semana?: EtiquetaSemana | '') => (semana ? { parametros: { semana } } : {})

export function useMarcarEnviado() {
  const api = useApi()
  const refrescar = useRefrescar()
  return useMutation<unknown, ErrorApi, { contactId: string; semana?: EtiquetaSemana | '' }>({
    mutationFn: ({ contactId, semana }) =>
      api.pedir(`/api/avisos-manuales/${contactId}/enviado`, { metodo: 'POST', ...deSemana(semana) }),
    onSuccess: refrescar,
  })
}

export interface DatosRespuesta {
  contactId: string
  respuesta: RespuestaAviso
  nota?: string
  /** Solo con `si_habitual`: fecha del reparto; el backend crea el albarán. */
  fecha?: string
  semana?: EtiquetaSemana | ''
}

export function useRegistrarRespuesta() {
  const api = useApi()
  const refrescar = useRefrescar()
  return useMutation<unknown, ErrorApi, DatosRespuesta>({
    mutationFn: ({ contactId, respuesta, nota, fecha, semana }) =>
      api.pedir(`/api/ciclo/respuestas/${contactId}`, {
        metodo: 'POST',
        cuerpo: { respuesta, ...(nota ? { nota } : {}), ...(fecha && respuesta === 'si_habitual' ? { fecha } : {}) },
        ...deSemana(semana),
      }),
    onSuccess: refrescar,
  })
}
